import { Input, Field } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { AttributeWrapper } from "./AttributeWrapper";
import {
  CraAttributeNumber,
  CodingAttributes,
  DistanceUnit,
} from "@keita/lanepatrol-sdk";
import { t } from "i18next";
import { parseAttributeInputValue } from "../../../../../../coding/utils/attribute-value";
import { useStores } from "../../../../../../shared/stores/RootStore";
import { useAttributeHighlight } from "../../hooks/useAttributeHighlight";
import { CraAttributeUtils } from "../../../../../../coding/utils/attribute-utils";
import { OsaNodeCoding } from "../../../../../../coding/models/OsaNodeCoding";

const NumberAttributeBase = ({
  attributeDefinition,
  disabled,
  nodeCoding,
  distanceUnit,
  compact,
}: {
  attributeDefinition: CraAttributeNumber;
  disabled: boolean;
  nodeCoding: OsaNodeCoding;
  compact: boolean;
  distanceUnit: DistanceUnit;
}) => {
  const { codingStore } = useStores();
  const highlighted = useAttributeHighlight(attributeDefinition);

  if (disabled) return null;

  const isSuggested = nodeCoding.suggestions.includes(attributeDefinition.name);
  const currentValue = nodeCoding.attributes[
    attributeDefinition.name as keyof CodingAttributes
  ] as null | number;
  const inputValue = currentValue === null ? "" : currentValue?.toString();

  return (
    <AttributeWrapper
      attributeDefinition={attributeDefinition}
      isSuggested={isSuggested}
      label={CraAttributeUtils.getLabel(attributeDefinition.name)}
      compact={compact}
    >
      <Field.Root invalid={isInvalid(currentValue, attributeDefinition)}>
        <Input
          size={compact ? "xs" : "md"}
          fontSize={compact ? "xs" : "md"}
          transitionDuration="0.2s"
          bgColor={highlighted ? `primary.100` : "white"}
          type="number"
          placeholder={
            attributeDefinition.name === "roadAADT"
              ? "AADT"
              : distanceUnit === DistanceUnit.Imperial
              ? "mph"
              : "km/h"
          }
          value={inputValue}
          min={attributeDefinition.min}
          max={attributeDefinition.max}
          onChange={(e) => {
            const value = e.target.value as string;

            const parsedValue = parseAttributeInputValue(
              attributeDefinition,
              value,
            ) as number | null;

            codingStore.codeAttribute(attributeDefinition.name, parsedValue);
          }}
          onKeyDown={(e) => e.stopPropagation()}
        />
        <Field.ErrorText>
          {t(`coding:common.invalidNumberInput`, {
            min: attributeDefinition.min ?? "-",
            max: attributeDefinition.max ?? "-",
          })}
        </Field.ErrorText>
      </Field.Root>
    </AttributeWrapper>
  );
};

export const NumberAttribute = observer(NumberAttributeBase);

const isInvalid = (
  value: number | null,
  attributeDefinition: CraAttributeNumber,
) => {
  if (value === null) return false;

  if (attributeDefinition.min !== undefined && value < attributeDefinition.min)
    return true;

  if (attributeDefinition.max !== undefined && value > attributeDefinition.max)
    return true;

  return false;
};
